import z from 'zod';
import {
  AllCharLabelSchema,
  CognitoUserIdSchema,
  UnixTimestampSchema,
  UUIDSchema,
} from '../common/schemas';
import { DBTimestampSchema } from '../common/sql';
import { ANSWER_SHEET_ROWS } from '../attemptSubmissions/constants';
import { AnswerSheetLetter } from '../attemptSubmissions/enums';
import {
  AttemptSubmissionAnswersSchema,
  AttemptSubmissionReadFullNameSchema,
  AttemptSubmissionReadPhoneDigitsSchema,
  AttemptSubmissionReadSetCodeSchema,
  DBAttemptSubmissionReadFullNameSchema,
  DBAttemptSubmissionReadPhoneDigitsSchema,
  DBAttemptSubmissionReadSetCodeSchema,
} from '../attemptSubmissions/schemas';
import {
  ATTEMPT_SCAN_ORIGINAL_FILENAME_MAX_LENGTH,
  ATTEMPT_SCAN_STATUS_DETAIL_MAX_LENGTH,
} from './constants';
import { AttemptScanMimeType, AttemptScanPageKind, AttemptScanStatus } from './enums';

/**
 * DB Schemas
 */

export const DBAttemptScanIdSchema = UUIDSchema;
export const DBAttemptScanOriginalFilenameSchema = AllCharLabelSchema.min(1).max(
  ATTEMPT_SCAN_ORIGINAL_FILENAME_MAX_LENGTH
);
export const DBAttemptScanStatusSchema = z.enum(AttemptScanStatus);
export const DBAttemptScanStatusDetailSchema = AllCharLabelSchema.min(1)
  .max(ATTEMPT_SCAN_STATUS_DETAIL_MAX_LENGTH)
  .nullable();
export const DBAttemptScanPageCountSchema = z.number().int().positive().nullable();
export const DBAttemptScanSheetCountSchema = z.number().int().min(0).nullable();
export const DBAttemptScanStalledReclaimsSchema = z.number().int().min(0);
export const DBAttemptScanPagesReadSchema = z.number().int().min(0);
export const DBAttemptScanFailedTriesSchema = z.number().int().min(0);

export const DBAttemptScanSchema = z.object({
  id: DBAttemptScanIdSchema,
  assetId: UUIDSchema,
  originalFilename: DBAttemptScanOriginalFilenameSchema,
  status: DBAttemptScanStatusSchema,
  statusDetail: DBAttemptScanStatusDetailSchema,
  pageCount: DBAttemptScanPageCountSchema,
  sheetCount: DBAttemptScanSheetCountSchema,
  stalledReclaims: DBAttemptScanStalledReclaimsSchema,
  pagesRead: DBAttemptScanPagesReadSchema,
  failedTries: DBAttemptScanFailedTriesSchema,
  uploadedBy: CognitoUserIdSchema.nullable(),
  dismissedAt: DBTimestampSchema.nullable(),
  dismissedBy: CognitoUserIdSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

/** A scan as a list reads it, with whether any sheet read from it remains. */
export const DBListedAttemptScanSchema = DBAttemptScanSchema.extend({
  hasSheets: z.boolean(),
});

export const DBAttemptScanPageNumberSchema = z.number().int().positive();
export const DBAttemptScanPageKindSchema = z.enum(AttemptScanPageKind).nullable();

export const DBAttemptScanPageSchema = z.object({
  id: UUIDSchema,
  scanId: DBAttemptScanIdSchema,
  pageNumber: DBAttemptScanPageNumberSchema,
  assetId: UUIDSchema,
  startsSheet: z.boolean(),
  kind: DBAttemptScanPageKindSchema,
  readFullName: DBAttemptSubmissionReadFullNameSchema,
  readPhoneDigits: DBAttemptSubmissionReadPhoneDigitsSchema,
  readSetCode: DBAttemptSubmissionReadSetCodeSchema,
  answers: z.array(z.enum(AnswerSheetLetter).nullable()).max(ANSWER_SHEET_ROWS),
  createdAt: DBTimestampSchema,
});

/**
 * Client Schemas
 */

export const AttemptScanIdSchema = DBAttemptScanIdSchema;
export const AttemptScanOriginalFilenameSchema = DBAttemptScanOriginalFilenameSchema.openapi({
  description: 'The name the file was uploaded under',
  example: 'Year 11 physics quiz.pdf',
});
export const AttemptScanStatusSchema = z.enum(AttemptScanStatus).openapi({
  description:
    'pending until the reader claims the scan, reading while it does, then read or failed',
  example: AttemptScanStatus.Read,
});
export const AttemptScanStatusDetailSchema = DBAttemptScanStatusDetailSchema.openapi({
  description: 'Why the scan could not be read',
  example: 'No response sheet was found in the file',
});
export const AttemptScanPageCountSchema = DBAttemptScanPageCountSchema;
export const AttemptScanSheetCountSchema = DBAttemptScanSheetCountSchema.openapi({
  description: 'The response sheets found in the file',
  example: 12,
});
export const AttemptScanStalledReclaimsSchema = DBAttemptScanStalledReclaimsSchema.openapi({
  description: 'How many times the scan was claimed again after its reader was gone',
  example: 0,
});
export const AttemptScanPagesReadSchema = DBAttemptScanPagesReadSchema.openapi({
  description:
    'The leading pages finished before the scan was last handed back, where reading resumes while the scan waits or is being read',
  example: 0,
});
export const AttemptScanFailedTriesSchema = DBAttemptScanFailedTriesSchema.openapi({
  description:
    'How many readers in a row the AI provider failed on at the page reading resumes from',
  example: 0,
});
export const AttemptScanMimeTypeSchema = z.enum(AttemptScanMimeType).openapi({
  description: 'The file type of a scan',
  example: AttemptScanMimeType.Pdf,
});

export const AttemptScanSchema = z.object({
  id: AttemptScanIdSchema,
  assetId: UUIDSchema,
  originalFilename: AttemptScanOriginalFilenameSchema,
  status: AttemptScanStatusSchema,
  statusDetail: AttemptScanStatusDetailSchema,
  pageCount: AttemptScanPageCountSchema,
  sheetCount: AttemptScanSheetCountSchema,
  stalledReclaims: AttemptScanStalledReclaimsSchema,
  pagesRead: AttemptScanPagesReadSchema,
  failedTries: AttemptScanFailedTriesSchema,
  uploadedBy: CognitoUserIdSchema.nullable(),
  dismissedAt: UnixTimestampSchema.nullable(),
  dismissedBy: CognitoUserIdSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const ListedAttemptScanSchema = AttemptScanSchema.extend({
  hasSheets: z.boolean().openapi({
    description:
      'Whether any sheet read from the scan remains, which keeps the scan from being deleted',
    example: false,
  }),
});

export const AttemptScanPageNumberSchema = DBAttemptScanPageNumberSchema.openapi({
  description: 'The page of the scan, the first being 1',
  example: 3,
});
export const AttemptScanPageStartsSheetSchema = z.boolean().openapi({
  description: "Whether the page carries the QR code printed on a sheet's first page",
  example: true,
});
export const AttemptScanPageKindSchema = z.enum(AttemptScanPageKind).nullable().openapi({
  description: 'What the page is, as read; null for a page that could not be read',
  example: AttemptScanPageKind.ResponseSheet,
});

export const AttemptScanPageSchema = z.object({
  id: UUIDSchema,
  scanId: AttemptScanIdSchema,
  pageNumber: AttemptScanPageNumberSchema,
  assetId: UUIDSchema,
  startsSheet: AttemptScanPageStartsSheetSchema,
  kind: AttemptScanPageKindSchema,
  readFullName: AttemptSubmissionReadFullNameSchema,
  readPhoneDigits: AttemptSubmissionReadPhoneDigitsSchema,
  readSetCode: AttemptSubmissionReadSetCodeSchema,
  answers: AttemptSubmissionAnswersSchema,
  createdAt: UnixTimestampSchema,
});
